import { ALL_LOCALES, EnGB, Locale, twoLetterLanguageCodes } from '@/constants/locales';

export type LocaleSource = 'picker' | 'query' | 'order' | 'navigator' | 'default';

export interface ResolvedLocale {
  locale: Locale;
  source: LocaleSource;
}

export interface LocaleContext {
  queryLocale?: string;
  orderLocale?: string;
}

const PICK_STORAGE_KEY = 'bk:selfService:localePick';

const isSupportedLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (ALL_LOCALES as readonly string[]).includes(value);

const readPick = (): Locale | null => {
  try {
    const raw = localStorage.getItem(PICK_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { locale?: unknown };
    return isSupportedLocale(parsed.locale) ? parsed.locale : null;
  } catch {
    clearLocalePick();
    return null;
  }
};

// Forgiving on the URL: accepts `it-IT`, `it_IT`, or just `it` (and case-
// insensitive). Order/picker tiers stay strict — they read canonical data.
const coerceQueryLocale = (tag: string | undefined): Locale | null => {
  if (!tag) return null;
  const normalized = tag.replace('_', '-');
  if (isSupportedLocale(normalized)) return normalized;
  const primary = normalized.toLowerCase().split('-')[0];
  return ALL_LOCALES.find(l => twoLetterLanguageCodes[l] === primary) ?? null;
};

const matchNavigator = (): Locale | null => {
  if (typeof navigator === 'undefined') return null;
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    if (typeof tag !== 'string' || tag.length === 0) continue;
    const primary = tag.toLowerCase().split('-')[0];
    const match = ALL_LOCALES.find(l => twoLetterLanguageCodes[l] === primary);
    if (match) return match;
  }
  return null;
};

/**
 * Resolution chain (highest precedence first):
 *   picker (persisted explicit pick) > query (?locale=) > order.locale
 *   > navigator.languages match > EnGB
 *
 * The picker tier is the only one persisted across sessions — every other
 * tier is cheap to re-derive on each visit, so storing them would just
 * complicate the override semantics.
 */
export const resolveLocale = (ctx: LocaleContext = {}): ResolvedLocale => {
  const picked = readPick();
  if (picked) return { locale: picked, source: 'picker' };

  const fromQuery = coerceQueryLocale(ctx.queryLocale);
  if (fromQuery) return { locale: fromQuery, source: 'query' };

  if (isSupportedLocale(ctx.orderLocale)) {
    return { locale: ctx.orderLocale, source: 'order' };
  }

  const fromNavigator = matchNavigator();
  if (fromNavigator) return { locale: fromNavigator, source: 'navigator' };

  return { locale: EnGB, source: 'default' };
};

export const persistLocalePick = (locale: Locale): void => {
  try {
    localStorage.setItem(
      PICK_STORAGE_KEY,
      JSON.stringify({ locale, source: 'picker', at: new Date().toISOString() }),
    );
  } catch {
    // Storage unavailable — pick still applies for this session via setCurrentLanguage.
  }
};

export const clearLocalePick = (): void => {
  try {
    localStorage.removeItem(PICK_STORAGE_KEY);
  } catch {
    // No-op.
  }
};
